import {
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FieldValue,
  type Firestore,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  Timestamp,
  collection,
  deleteField,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  increment,
  initializeFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

import { getApp } from './config';

let firestoreInstance: Firestore | null = null;

/**
 * Get the Firestore instance (singleton). Importing this module pulls in
 * `firebase/firestore` (+ `firebase/app`) — keep it out of bundles (e.g. the
 * public bio) that don't do client-side Firestore reads/listening.
 */
export function getFirestoreInstance(): Firestore {
  if (firestoreInstance) {
    return firestoreInstance;
  }

  const firebaseApp = getApp();
  try {
    firestoreInstance = initializeFirestore(firebaseApp, {
      ignoreUndefinedProperties: true,
      // Firestore's default streaming transport does not complete inside a
      // WKWebView — the request hangs, so `getDoc` never settles and a `catch`
      // cannot save the caller (it strands the native app's login on "Signing
      // you in…"). Auto-detect probes the connection and falls back to long
      // polling only where it is needed, so the browser keeps the faster
      // transport.
      experimentalAutoDetectLongPolling: true,
    });
  } catch {
    // If already initialized, get the existing instance
    firestoreInstance = getFirestore(firebaseApp);
  }

  return firestoreInstance;
}

// Re-export Firestore instance
export const firestore = getFirestoreInstance;

// Re-export Firestore functions
export {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
};

// Re-export Firestore types
export type {
  Firestore,
  DocumentData,
  DocumentSnapshot,
  QuerySnapshot,
  QueryDocumentSnapshot,
  DocumentReference,
  CollectionReference,
  Query,
  QueryConstraint,
  FieldValue,
};

// Field value helpers (matching legacy API)
export const fieldValueDelete = () => deleteField();
export const fieldValueIncrement = increment;
// serverTimestamp() returns a FieldValue, not a Timestamp
// For compatibility with legacy code that expects a Timestamp type
export const serverNow = () => serverTimestamp();
export const clientNow = () => Timestamp.now();
export const timeStampFrom = ({
  seconds,
  nanoseconds,
}: {
  seconds: number;
  nanoseconds: number;
}) => new Timestamp(seconds, nanoseconds);
export const timeStampFromDate = (date: Date) => Timestamp.fromDate(date);

export { Timestamp };

// Field path helpers
export const documentIdPath = () => documentId();
